/**
 * Colour variants — the swatch strip under a category card.
 *
 * Three jobs: preview a colour in the card image on hover (or the first tap on
 * touch), make the strip usable and obviously scrollable, and drop swatches
 * whose photo is missing.
 *
 * Everything is delegated from `document` on purpose. The product filter
 * (`diesite-product-filter`) replaces the entire `<ul class="products">` node on
 * first paint and again on every filter change, so anything bound to loop
 * elements themselves is thrown away before a customer ever hovers.
 */
const SWAP_ATTRIBUTE = 'data-hw-colour-swap';

/** The swatch the pointer is currently on, so a slow image load can't win a race. */
let activeSwatch = null;

/**
 * @param {HTMLElement} swatch
 * @returns {HTMLImageElement|null}
 */
function cardImage(swatch) {
    const card = swatch.closest('li.product');

    return card ? card.querySelector('figure img') : null;
}

/**
 * Show the hovered colour's own name and price on the card.
 *
 * Previewing the photo alone leaves the card describing a different rug to the
 * one being shown — and the price can differ between colours.
 *
 * @param {HTMLElement} swatch
 * @param {boolean} preview true to show the swatch's, false to put the card's back.
 */
function previewCardDetails(swatch, preview) {
    const card = swatch.closest('li.product');

    if (!card) {
        return;
    }

    [
        { element: card.querySelector('h2.woocommerce-loop-product__title'), value: swatch.dataset.hwColourTitle, html: false },
        { element: card.querySelector('span.price'), value: swatch.dataset.hwColourPrice, html: true },
    ].forEach(function (target) {
        if (!target.element || !target.value) {
            return;
        }

        if (target.element.dataset.hwColourOriginal === undefined) {
            target.element.dataset.hwColourOriginal = target.html
                ? target.element.innerHTML
                : target.element.textContent;
        }

        const next = preview ? target.value : target.element.dataset.hwColourOriginal;

        if (target.html) {
            target.element.innerHTML = next;
        } else {
            target.element.textContent = next;
        }
    });
}

/**
 * @param {HTMLImageElement} image
 * @param {string} source
 * @param {HTMLElement} swatch
 */
function preview(image, source, swatch) {
    if (image.dataset.hwColourOriginal === undefined) {
        image.dataset.hwColourOriginal = image.getAttribute('src') || '';
        image.dataset.hwColourOriginalSrcset = image.getAttribute('srcset') || '';
        image.dataset.hwColourOriginalSizes = image.getAttribute('sizes') || '';
    }

    if (image.getAttribute('src') === source) {
        return;
    }

    const apply = function () {
        // The pointer may have moved on while the variant was downloading.
        if (activeSwatch !== swatch) {
            return;
        }

        // The variant ships one size, so the responsive attributes have to go
        // with it or the browser keeps picking a candidate from the old srcset.
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.setAttribute('src', source);
    };

    // Swapping src before the variant has decoded leaves the card blank or
    // showing the previous colour for as long as the download takes. Load it
    // out of band first, then swap in one frame. Cached variants apply
    // immediately.
    const loader = new Image();

    loader.src = source;

    if (loader.complete) {
        apply();

        return;
    }

    loader.decode().then(apply, apply);
}

/**
 * @param {HTMLImageElement} image
 */
function restore(image) {
    if (image.dataset.hwColourOriginal === undefined) {
        return;
    }

    image.setAttribute('src', image.dataset.hwColourOriginal);

    if (image.dataset.hwColourOriginalSrcset) {
        image.setAttribute('srcset', image.dataset.hwColourOriginalSrcset);
    }

    if (image.dataset.hwColourOriginalSizes) {
        image.setAttribute('sizes', image.dataset.hwColourOriginalSizes);
    }
}

/**
 * Flags whether a strip has more to show, and which way.
 *
 * The classes drive the edge fades — without them a strip that continues past
 * the card edge looks like it simply ends there.
 *
 * @param {HTMLElement} track
 */
function markOverflow(track) {
    const strip = track.parentElement;
    const overflow = track.scrollWidth - track.clientWidth;

    // Sub-pixel widths mean the maths rarely lands exactly on zero.
    strip.classList.toggle('is-overflowing', overflow > 1);
    strip.classList.toggle('at-start', track.scrollLeft <= 1);
    strip.classList.toggle('at-end', track.scrollLeft >= overflow - 1);
}

const NAV_LABELS = { prev: 'Vorige kleuren', next: 'Volgende kleuren' };

/**
 * Add or remove the arrow buttons for a strip.
 *
 * They are built here rather than in PHP because they are useless without JS,
 * and because only the strips that actually overflow need them — on a
 * hundred-card listing that is the difference between two spare nodes per card
 * and none.
 *
 * @param {HTMLElement} track
 */
function syncNav(track) {
    const strip = track.parentElement;
    const overflows = strip.classList.contains('is-overflowing');
    const existing = strip.querySelectorAll('.hw-colours__nav');

    if (!overflows) {
        existing.forEach(function (button) {
            button.remove();
        });

        return;
    }

    if (existing.length) {
        return;
    }

    ['prev', 'next'].forEach(function (direction) {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'hw-colours__nav hw-colours__nav--' + direction;
        button.dataset.hwColourNav = direction;

        // Every swatch is a link already, so keyboard users reach the whole
        // strip by tabbing through it — the browser scrolls each one into view.
        // Two extra tab stops per card would only be noise.
        button.tabIndex = -1;
        button.setAttribute('aria-hidden', 'true');
        button.setAttribute('title', NAV_LABELS[direction]);

        strip.appendChild(button);
    });
}

/**
 * Measure any strip that has not been measured yet.
 *
 * Marking each track means the usual case — nothing changed — costs one
 * querySelectorAll that matches nothing, so this can safely run on every DOM
 * mutation.
 */
function measurePending() {
    document.querySelectorAll('.hw-colours__track:not([data-hw-measured])').forEach(function (track) {
        track.dataset.hwMeasured = '1';
        markOverflow(track);
        syncNav(track);
    });
}

/** Widths change on resize, so every strip has to be measured again. */
function measureAll() {
    document.querySelectorAll('.hw-colours__track').forEach(function (track) {
        track.dataset.hwMeasured = '1';
        markOverflow(track);
        syncNav(track);
    });
}

/** Coalesce bursts of mutations into one layout read per frame. */
let refreshQueued = false;

function queueRefresh(all) {
    if (all) {
        document.querySelectorAll('.hw-colours__track').forEach(function (track) {
            delete track.dataset.hwMeasured;
        });
    }

    if (refreshQueued) {
        return;
    }

    refreshQueued = true;

    const run = function () {
        if (!refreshQueued) {
            return;
        }

        refreshQueued = false;
        measurePending();
    };

    // Frame-aligned while the tab is visible, but a background tab gets no
    // frames at all — and a strip measured only once the customer comes back
    // would have spent that whole time with no scroll hint.
    requestAnimationFrame(run);
    setTimeout(run, 100);
}

/**
 * Watch for new strips appearing.
 *
 * The product filter re-renders the listing, but pinning this to the container
 * it swaps means depending on exactly which node it replaces — which turned out
 * not to be the one the markup suggests. Watching the document instead is
 * indifferent to that, and stays cheap because a pass over unchanged DOM
 * matches no tracks.
 */
function observeListing() {
    if (typeof MutationObserver === 'undefined') {
        return;
    }

    new MutationObserver(function () {
        queueRefresh(false);
    }).observe(document.body, { childList: true, subtree: true });
}

/**
 * Point the card's own links at the previewed colour, or back at the card's.
 *
 * On a phone the preview stays up after the tap, so the photo and title the
 * customer taps next show that colour — they should open it too.
 *
 * @param {HTMLElement} swatch
 * @param {boolean} preview
 */
function previewCardLinks(swatch, preview) {
    const card = swatch.closest('li.product');

    if (!card) {
        return;
    }

    card.querySelectorAll('a.woocommerce-loop-product__link').forEach(function (link) {
        if (link.dataset.hwColourOriginal === undefined) {
            link.dataset.hwColourOriginal = link.getAttribute('href') || '';
        }

        link.setAttribute('href', preview ? swatch.getAttribute('href') : link.dataset.hwColourOriginal);
    });
}

/**
 * Show a swatch's colour on its card.
 *
 * @param {HTMLElement} swatch
 */
function showSwatch(swatch) {
    activeSwatch = swatch;
    previewCardDetails(swatch, true);
    previewCardLinks(swatch, true);

    const image = cardImage(swatch);

    if (image) {
        preview(image, swatch.getAttribute(SWAP_ATTRIBUTE), swatch);
    }
}

/**
 * Put the card back the way the page rendered it.
 *
 * @param {HTMLElement} swatch
 */
function hideSwatch(swatch) {
    if (activeSwatch === swatch) {
        activeSwatch = null;
    }

    previewCardDetails(swatch, false);
    previewCardLinks(swatch, false);

    const image = cardImage(swatch);

    if (image) {
        restore(image);
    }
}

document.addEventListener('pointerover', function (event) {
    // Touch taps fire pointerover too; taps are handled on click instead, where
    // a scroll of the strip has already been ruled out.
    if (event.pointerType === 'touch') {
        return;
    }

    const swatch = event.target.closest('[' + SWAP_ATTRIBUTE + ']');

    if (swatch) {
        showSwatch(swatch);
    }
});

document.addEventListener('pointerout', function (event) {
    if (event.pointerType === 'touch') {
        return;
    }

    const swatch = event.target.closest('[' + SWAP_ATTRIBUTE + ']');

    if (!swatch) {
        return;
    }

    // Moving between two swatches, or onto a swatch's own image: the next
    // pointerover repaints anyway, so only restore once the pointer has left
    // the strip for good.
    const strip = swatch.closest('.hw-colours');

    if (strip && event.relatedTarget && strip.contains(event.relatedTarget)) {
        return;
    }

    hideSwatch(swatch);
});

/**
 * Touch has no hover, so on a phone the first tap on a colour does what
 * hovering does on desktop — preview it in the card — and a second tap on the
 * same colour opens it.
 *
 * The pointer type is read from pointerdown because iOS Safari's click event
 * does not say what produced it.
 */
let lastPointerType = '';

/** The swatch a tap is currently previewing. */
let tappedSwatch = null;

document.addEventListener('pointerdown', function (event) {
    lastPointerType = event.pointerType;
}, true);

document.addEventListener('click', function (event) {
    if (lastPointerType !== 'touch') {
        return;
    }

    const swatch = event.target.closest('[' + SWAP_ATTRIBUTE + ']');

    if (!swatch) {
        // Tapping anywhere but the card being previewed is the touch version of
        // the pointer leaving it.
        if (tappedSwatch && !tappedSwatch.closest('li.product')?.contains(event.target)) {
            hideSwatch(tappedSwatch);
            tappedSwatch = null;
        }

        return;
    }

    if (swatch === tappedSwatch) {
        return;
    }

    event.preventDefault();

    if (tappedSwatch) {
        hideSwatch(tappedSwatch);
    }

    tappedSwatch = swatch;
    showSwatch(swatch);
});

// `error` does not bubble, so this has to run in the capture phase.
document.addEventListener('error', function (event) {
    const image = event.target;

    if (!image || image.tagName !== 'IMG') {
        return;
    }

    const swatch = image.closest('.hw-colours__item');

    if (!swatch) {
        return;
    }

    // A few product photos are missing from the CDN. An empty bordered box
    // helps nobody, so drop the swatch and, if nothing survives, the strip.
    const strip = swatch.closest('.hw-colours');

    swatch.remove();

    if (strip && !strip.querySelector('.hw-colours__item')) {
        strip.remove();

        return;
    }

    // One swatch fewer can be the difference between a strip that overflows and
    // one that fits, so this track needs measuring again rather than keeping the
    // verdict it was given a moment ago.
    const track = strip ? strip.querySelector('.hw-colours__track') : null;

    if (track) {
        delete track.dataset.hwMeasured;
    }

    queueRefresh(false);
}, true);

// Scroll position changes come from the tracks themselves; scroll events do not
// bubble but they do capture.
document.addEventListener('scroll', function (event) {
    const track = event.target;

    if (track instanceof Element && track.classList.contains('hw-colours__track')) {
        markOverflow(track);
    }
}, true);

document.addEventListener('click', function (event) {
    const button = event.target.closest('.hw-colours__nav');

    if (!button) {
        return;
    }

    event.preventDefault();

    const track = button.parentElement.querySelector('.hw-colours__track');

    if (!track) {
        return;
    }

    // Just short of a full width, so the swatch that was half-visible at the
    // edge stays on screen as an anchor rather than jumping past.
    const step = Math.max(60, Math.round(track.clientWidth * 0.8));

    // Smooth scrolling never advances in a background tab, and someone who has
    // asked for less motion should not get an animation either — in both cases
    // the jump still has to happen.
    const animate = !document.hidden
        && !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    track.scrollBy({
        left: button.dataset.hwColourNav === 'prev' ? -step : step,
        behavior: animate ? 'smooth' : 'auto',
    });
});

// A horizontal strip is unreachable with a plain mouse wheel, so translate
// vertical wheel movement while the pointer is over one — but only while the
// strip still has somewhere to go, otherwise the page stops scrolling.
document.addEventListener('wheel', function (event) {
    const track = event.target.closest ? event.target.closest('.hw-colours__track') : null;

    if (!track || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
        return;
    }

    const overflow = track.scrollWidth - track.clientWidth;
    const target = Math.max(0, Math.min(overflow, track.scrollLeft + event.deltaY));

    // Nothing left to give in that direction — let the page scroll instead of
    // trapping the wheel inside the strip. The tolerance matters: a fully
    // scrolled strip commonly sits a fraction of a pixel short of its own
    // maximum, which is not movement worth swallowing a scroll for.
    if (Math.abs(target - track.scrollLeft) < 1) {
        return;
    }

    event.preventDefault();
    track.scrollLeft = target;
}, { passive: false });

/**
 * Carry the customer's size and underlay choice across to the colour they pick.
 *
 * Picking a new colour after choosing "200 x 290 cm, met onderkleed" should not
 * silently drop that. WooCommerce preselects a variation from
 * `attribute_*` query args, so the swatch links are rewritten as the form
 * changes.
 *
 * The size only travels to colours that sell the same sizes — the flag comes
 * from the server, which knows each colour's range. The underlay is safe
 * everywhere: no rug in the catalogue offers a different set to its siblings.
 */
const SIZE_ATTRIBUTE = 'attribute_pa_maat';
const UNDERLAY_ATTRIBUTE = 'attribute_pa_onderkleed';

function readJson(value, fallback) {
    if (!value) {
        return fallback;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        return fallback;
    }
}

/**
 * What the chosen size becomes on one particular colour.
 *
 * Mirrors GroupBuilder::sizeArgsFor() — the listing resolves this server-side,
 * where the size comes from the filter; here it has to follow the customer as
 * they change it.
 *
 * @returns {Object} query args to add, possibly empty.
 */
function dimensionArgs(dims) {
    if (dims.shape === 'round') {
        return { hw_diagonal: Math.round(dims.d || 0) };
    }

    return { hw_width: Math.round(dims.w || 0), hw_length: Math.round(dims.l || 0) };
}

function sizeArgsFor(chosen, dims, chosenIsCustom, sizes, custom) {
    if (!chosen) {
        return {};
    }

    if (sizes.indexOf(chosen) !== -1) {
        const args = { [SIZE_ATTRIBUTE]: chosen };

        // Made-to-measure still needs its dimensions, or the customer arrives at
        // an empty measurement form having already filled one in.
        return chosenIsCustom && dims ? Object.assign(args, dimensionArgs(dims)) : args;
    }

    // Not sold in that size, but made to measure — same rug, same dimensions,
    // cut to order — beats dropping the size the customer had settled on.
    if (!dims) {
        return {};
    }

    const round = dims.shape === 'round';
    const slug = custom[round ? 'round' : 'rect'];

    // A round rug has one measurement, so a colour that only does rectangular
    // made-to-measure can still make it: a square of that diameter. There is no
    // equivalent the other way — a rectangle has two measurements and a circle
    // cannot hold both.
    if (!slug && round && custom.rect) {
        const diameter = Math.round(dims.d || 0);

        return { [SIZE_ATTRIBUTE]: custom.rect, hw_width: diameter, hw_length: diameter };
    }

    if (!slug) {
        return {};
    }

    return Object.assign({ [SIZE_ATTRIBUTE]: slug }, dimensionArgs(dims));
}

/**
 * The dimensions typed into the made-to-measure form, if it is showing.
 *
 * Selecting "Maatwerk" leaves the size itself dimensionless — what the customer
 * actually chose is whatever they then key into these inputs, so that is what
 * has to travel.
 */
function typedDimensions() {
    const round = document.querySelector('#custom-round-select');
    const rect = document.querySelector('#custom-select');
    const visible = (el) => el && el.offsetParent !== null;
    const number = (el) => {
        const value = el ? parseFloat(el.value) : NaN;

        return Number.isFinite(value) && value > 0 ? value : null;
    };

    if (visible(round)) {
        const d = number(round.querySelector('[name="diagonal"]'));

        return d ? { shape: 'round', d } : null;
    }

    if (visible(rect)) {
        const l = number(rect.querySelector('[name="length"]'));
        const w = number(rect.querySelector('[name="width"]'));

        // Kept as typed rather than sorted: these are the customer's own length
        // and width, and the form on the other side reads them by name.
        return l && w ? { shape: 'rect', w, l } : null;
    }

    return null;
}

function syncVariantLinks() {
    const form = document.querySelector('form.variations_form');
    const strip = document.querySelector('.hw-colours--single');

    if (!form || !strip) {
        return;
    }

    const track = strip.querySelector('.hw-colours__track');
    const dimensions = readJson(track && track.dataset.hwDims, {});
    const sizeField = form.querySelector('[name="' + SIZE_ATTRIBUTE + '"]');
    const underlayField = form.querySelector('[name="' + UNDERLAY_ATTRIBUTE + '"]');
    const chosenSize = sizeField ? sizeField.value : '';
    const chosenUnderlay = underlayField ? underlayField.value : '';

    // A size with numbers in its name describes itself; "Maatwerk" does not, so
    // its dimensions come from what the customer typed.
    const ownDimensions = dimensions[chosenSize] || null;
    const chosenIsCustom = !ownDimensions;
    const dims = ownDimensions || (chosenIsCustom ? typedDimensions() : null);

    strip.querySelectorAll('a.hw-colours__item').forEach(function (swatch) {
        if (!swatch.dataset.hwBaseHref) {
            swatch.dataset.hwBaseHref = swatch.getAttribute('href');
        }

        const url = new URL(swatch.dataset.hwBaseHref, window.location.origin);
        const args = sizeArgsFor(
            chosenSize,
            dims,
            chosenIsCustom,
            readJson(swatch.dataset.hwSizes, []),
            readJson(swatch.dataset.hwCustom, {})
        );

        Object.keys(args).forEach(function (name) {
            url.searchParams.set(name, args[name]);
        });

        // Safe everywhere: no rug offers a different underlay set to its
        // siblings.
        if (chosenUnderlay) {
            url.searchParams.set(UNDERLAY_ATTRIBUTE, chosenUnderlay);
        }

        swatch.setAttribute('href', url.pathname + url.search);
    });
}

/**
 * Listen for the customer changing a variation.
 *
 * This has to go through jQuery. WooCommerce and woo-variation-swatches raise
 * the change with jQuery's `.trigger()`, which walks jQuery's own handler
 * registry rather than dispatching a DOM event — a native listener on
 * `document` never hears it, and the links silently keep whatever size the page
 * loaded with.
 */
function bindVariationEvents() {
    const $ = window.jQuery;

    if ($) {
        $(document.body)
            .on('change', 'form.variations_form [name^="attribute_"]', syncVariantLinks)
            // Fired once WooCommerce has resolved (or cleared) the variation, so
            // this also covers "Wis" and the plugin's own preselection.
            .on(
                'found_variation reset_data woocommerce_variation_has_changed',
                'form.variations_form',
                syncVariantLinks
            );

        return;
    }

    // No jQuery on the page: fall back to the native event.
    document.addEventListener('change', function (event) {
        if (event.target.name && event.target.name.indexOf('attribute_') === 0) {
            syncVariantLinks();
        }
    });
}

/** The measurement fields are typed into, so these are ordinary DOM events. */
function bindMeasurementEvents() {
    ['input', 'change'].forEach(function (type) {
        document.addEventListener(type, function (event) {
            if (event.target.closest('#custom-select, #custom-round-select')) {
                syncVariantLinks();
            }
        });
    });
}

window.addEventListener('resize', function () {
    queueRefresh(true);
});

function start() {
    measureAll();
    observeListing();

    // WooCommerce preselects default or URL-supplied attributes on load, so the
    // links need to reflect that before the customer touches anything.
    bindVariationEvents();
    bindMeasurementEvents();
    syncVariantLinks();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
} else {
    start();
}
